import { Skeleton } from "@/components/ui/skeleton";
import InfoTooltip from "@/components/ui/InfoTooltip";
import { WidgetTitle } from "@/components/widgets/WidgetTitle";
import { useDashboard } from "@/contexts/DashboardContext";
import { cn } from "@/lib/utils";
import { TICK_CONFIG, getTooltipCursorFill, CHART_MARGINS } from "@/lib/chartConfig";
import { formatTooltipTime, formatXAxisTick, GroupByType } from "@/utils/dateUtils";
import React, { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { Info } from "lucide-react";

interface CallVolumeWidgetData {
  time: Date;
  inboundCall: number;
  outboundCall: number;
}

/**
 *
 * @param dateRangeType
 * @param dateRange
 *
 * - When dateRange is the same day, return "hour"
 * - When dateRange is the same month, return "day"
 * - When dateRange is the same year, return "month"
 * - When dateRange is not the same year, return "day"
 *
 * @returns
 *
 */
const getGroupByFromDateRange = (dateRangeType: string, dateRange?: { from: Date, to: Date }): GroupByType => {
  if (!dateRange) return "day";

  const isDateRangeTheSameYear = dateRange.from.getFullYear() === dateRange.to.getFullYear();
  const isDateRangeTheSameMonth = dateRange.from.getMonth() === dateRange.to.getMonth() && isDateRangeTheSameYear;
  const isDateRangeTheSameDay = dateRange.from.getDate() === dateRange.to.getDate() && isDateRangeTheSameMonth && isDateRangeTheSameYear;

  if (isDateRangeTheSameDay) {
    return "hour";
  } else if (isDateRangeTheSameYear) {
    return "day";
  } else {
    return "daydiffyear";
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomTooltip = ({ active, payload, label, dateRange, theme, groupBy }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-gray-800 p-3 border dark:border-gray-600 rounded shadow-lg">
        <p className="font-semibold dark:text-white">{formatTooltipTime(label, groupBy)}</p>
        <p style={{ color: 'hsl(var(--brand-navy))' }}>Offered calls: {payload[0].value}</p>
      </div>
    );
  }
  return null;
};

interface CallVolumeWidgetProps {
  data: CallVolumeWidgetData[];
  dateRangeType: string;
  dateRange: { from: Date, to: Date };
  icon?: React.ReactNode;
  loading?: boolean;
  error?: string;
}

// Build placeholder buckets (0 calls) spanning dateRange so axes still render when there's no data
const generateEmptyBuckets = (dateRange: { from: Date, to: Date }, groupBy: GroupByType): CallVolumeWidgetData[] => {
  const buckets: CallVolumeWidgetData[] = [];
  const cursor = new Date(dateRange.from);

  if (groupBy === 'hour') {
    cursor.setMinutes(0, 0, 0);
    for (let i = 0; i < 24; i++) {
      buckets.push({ time: new Date(cursor), inboundCall: 0, outboundCall: 0 });
      cursor.setHours(cursor.getHours() + 1);
    }
    return buckets;
  }

  cursor.setHours(0, 0, 0, 0);
  const end = new Date(dateRange.to);
  end.setHours(0, 0, 0, 0);
  while (cursor.getTime() <= end.getTime()) {
    buckets.push({ time: new Date(cursor), inboundCall: 0, outboundCall: 0 });
    cursor.setDate(cursor.getDate() + 1);
  }
  return buckets;
};

const CallVolumeWidget: React.FC<CallVolumeWidgetProps> = ({ data, dateRangeType, dateRange, loading }) => {
  const { theme } = useDashboard();
  const [tickGap, setTickGap] = useState<number>(1);

  // Convert data to local timezone and group by month if needed
  const processedData = data?.map(item => ({
    ...item,
    time: new Date(item.time)
  }));

  // Update dateRange based on data if available
  if (processedData && processedData.length > 0) {
    const times = processedData.map(item => item.time.getTime());
    const minTime = new Date(Math.min(...times));
    const maxTime = new Date(Math.max(...times));
    dateRange = {
      from: minTime,
      to: maxTime
    };
  }

  const groupBy: GroupByType = getGroupByFromDateRange(dateRangeType, dateRange);

  // No data at all: show empty axes (still 0..10 scale) instead of a "no data" message
  const chartData = processedData?.length ? processedData : generateEmptyBuckets(dateRange, groupBy);

  // Check if all data points are 0
  const isAllDataZero = chartData?.every(item =>
    item.inboundCall === 0
  ) ?? false;

  // Calculate tickGap based on container width and data points
  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current && chartData?.length) {
      const items = chartData.length;
      const width = (containerRef.current.clientWidth || containerRef.current.offsetWidth || 600) - 60 - 25;
      const required = groupBy === 'daydiffyear' ? 80 : 60;
      const calculated = Math.ceil(required / (width / items));
      setTickGap(calculated);
    }
  }, [groupBy, chartData]);

  return (
    <div ref={containerRef} className={cn(
      "w-full min-w-[200px] rounded-lg border flex flex-col bg-white dark:bg-[#0C3451] dark:text-white dark:border-[#0C3451] p-3 h-full min-h-[300px]"
    )}>
      <div className="flex flex-row items-center justify-between gap-2">
        <div className="flex flex-row items-center gap-2 min-w-0">
          <WidgetTitle className="truncate">Call Volume</WidgetTitle>
          <InfoTooltip
            content="The total number of inbound calls within the given time."
            iconClassName="text-primary dark:text-white"
          />
        </div>
      </div>
      <div className="flex-1 min-h-0">
        {loading ? (
          <Skeleton className="h-full w-full" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={CHART_MARGINS.default}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="time"
                tick={TICK_CONFIG}
                tickFormatter={(value) => formatXAxisTick(value, groupBy)}
                stroke="currentColor"
                interval="equidistantPreserveStart"
              />
              <YAxis
                tick={TICK_CONFIG}
                stroke="currentColor"
                domain={isAllDataZero ? [0, 10] : undefined}
                ticks={isAllDataZero ? [0, 2, 4, 6, 8, 10] : undefined}
                allowDecimals={false}
              />
              <Tooltip content={(props) => <CustomTooltip {...props} dateRange={dateRangeType} theme={theme} groupBy={groupBy} />} cursor={getTooltipCursorFill(theme)} />
              <Bar dataKey="inboundCall" name="Inbound" fill="hsl(var(--brand-navy))" radius={[4, 4, 0, 0]} maxBarSize={24} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default CallVolumeWidget;
